// ---------------------------------------------------------------------------
// YouTube → URL de EMBED (privacy-enhanced) + orientação do player.
//
// Fonte única para os blocos de vídeo do site. Aceita o que o CRM legado grava
// de fato em `tbl_empreendimento.VideosEmpre` (e afins):
//   • id cru de 11 caracteres          → dQw4w9WgXcQ
//   • youtu.be/<id>                    (com ou sem ?t=)
//   • youtube.com/watch?v=<id>         (com &list=, &t=, etc.)
//   • youtube.com/embed/<id>
//   • youtube.com/shorts/<id>          → VERTICAL (9:16)
//   • youtube.com/live/<id>
// Vários separados por vírgula: usa o PRIMEIRO (é o que a ficha do imóvel já faz).
//
// Sempre em youtube-nocookie.com: sem cookie de rastreio antes de o visitante
// dar play — casa com o banner de consentimento do site.
// ---------------------------------------------------------------------------

export type YoutubeEmbed = {
    src: string;
    /** Shorts (9:16) — o player é montado em pé e centralizado. */
    vertical: boolean;
};

const ID_YOUTUBE = /^[\w-]{11}$/;

const montaEmbed = (id: string, vertical = false): YoutubeEmbed | null => {
    const limpo = String(id || '').trim();
    if (!ID_YOUTUBE.test(limpo)) return null;
    return {
        src: `https://www.youtube-nocookie.com/embed/${limpo}?rel=0&modestbranding=1&playsinline=1`,
        vertical,
    };
};

export const parseYoutubeEmbed = (raw?: string | null): YoutubeEmbed | null => {
    const bruto = String(raw || '').split(',')[0]?.trim() || '';
    if (!bruto) return null;
    if (ID_YOUTUBE.test(bruto)) return montaEmbed(bruto);

    let url: URL;
    try {
        url = new URL(/^https?:\/\//i.test(bruto) ? bruto : `https://${bruto}`);
    } catch {
        return null; // texto que não é URL nem id → nada a renderizar
    }

    const host = url.hostname.toLowerCase().replace(/^(www|m)\./, '');
    const partes = url.pathname.split('/').filter(Boolean);

    if (host === 'youtu.be') return montaEmbed(partes[0] || '');
    if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
        const v = url.searchParams.get('v');
        if (v) return montaEmbed(v);
        if (partes[0] === 'shorts') return montaEmbed(partes[1] || '', true);
        if (partes[0] === 'embed' || partes[0] === 'live' || partes[0] === 'v') {
            return montaEmbed(partes[1] || '');
        }
    }
    return null;
};
